import { useId, useRef } from "react";
import { useIntl } from "react-intl";
import { AiDataCaptureCaption } from "../AiDataCaptureCaption";
import { DownloadActionMenu } from "./DownloadActionMenu";
import { useExportToPng } from "../export-conversation/useExportToPng";
import { downloadTableAsCsv, sortTableRows } from "../ai-chat-message-utils";
import type { TablePayload, TableSortState } from "../ai-chat-message-utils";

interface AiTableProps {
  payload: TablePayload;
  readOnly?: boolean;
  sort?: TableSortState;
  onSortChange: (sort: TableSortState) => void;
}

const getNextSort = (state: TableSortState, column: string): TableSortState => {
  if (column === state.column) {
    return { ...state, dir: state.dir === "asc" ? "desc" : "asc" };
  }
  return { column, dir: "asc" };
};

type SortIconState = "none" | "ascending" | "descending";

const SORT_ICON_PATHS: Record<SortIconState, string> = {
  none: "M9,3L5,7H8V14H10V7H13M16,17V10H14V17H11L15,21L19,17H16Z",
  ascending: "M5 9L6.41 10.41L11 5.83V22H13V5.83L17.59 10.42L19 9L12 2L5 9Z",
  descending: "M19 15L17.59 13.59L13 18.17V2H11V18.17L6.41 13.58L5 15L12 22L19 15Z",
};

const SortIcon = ({ state }: { state: SortIconState }) => (
  <svg
    className="sort-button__icon tcw-export-exclude"
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
  >
    <path d={SORT_ICON_PATHS[state]} fill="currentColor" />
  </svg>
);


export const AiTable = ({
  payload,
  readOnly = false,
  sort,
  onSortChange,
}: AiTableProps) => {
  const intl = useIntl();
  const { columns, rows, title } = payload;
  const titleId = useId();
  const elementRef = useRef<HTMLDivElement>(null);
  const { isExporting, handleExport } = useExportToPng({
    elementRef,
    fileTitle: title || "Currents_Conversation_Table",
    errorMessage: intl.formatMessage({ id: "app.toolboxChat.action.downloadPngError" }),
  });

  const currentSort: TableSortState = sort ?? { column: columns[0] ?? null, dir: "asc" };
  const { column: sortColumn, dir: sortDir } = currentSort;

  const sortedRows = sortTableRows(columns, rows, sortColumn, sortDir);

  const sortAnnouncement = sortColumn
    ? intl.formatMessage(
        {
          id:
            sortDir === "asc"
              ? "app.toolboxChat.table.sortAscending"
              : "app.toolboxChat.table.sortDescending",
        },
        { column: sortColumn },
      )
    : "";
  return (
    <div ref={elementRef}>
      <div className="paper-card tcw-table-card">
        <div className="header tcw-table-header">
          {title && (
            <h3 id={titleId} className="title">
              {title}
            </h3>
          )}
          {!readOnly && (
            <DownloadActionMenu
              tooltip={intl.formatMessage({ id: "app.toolboxChat.action.downloadTable" })}
              onDownloadPng={handleExport}
              onDownloadCsv={() => downloadTableAsCsv(columns, sortedRows, title)}
              disabled={isExporting}
            />
          )}
        </div>
        <div className="body">
          <table className="data-table" aria-labelledby={title ? titleId : undefined}>
            <thead>
              <tr>
                {columns.map((col) => {
                  const isSorted = col === sortColumn;
                  const sortState: SortIconState = isSorted
                    ? sortDir === "asc"
                      ? "ascending"
                      : "descending"
                    : "none";
                  return (
                    <th
                      key={col}
                      scope="col"
                      className="dataheadercell sortable"
                      aria-sort={isSorted ? sortState : undefined}
                    >
                      <button
                        type="button"
                        className="sort-button"
                        onClick={() => onSortChange(getNextSort(currentSort, col))}
                      >
                        {col}
                        {!readOnly && <SortIcon state={sortState} />}
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex} className="datacell" data-th={columns[cellIndex]}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="sr-only" aria-live="polite">
            {sortAnnouncement}
          </div>
        </div>
        <AiDataCaptureCaption />
      </div>
    </div>
  );
};
